/**
 * What the pill on a line of Just added (`AddResultPill`) says, and which bins it offers:
 * separate from the component so this is plain data a test can read.
 */

import type { AddEntry, Kind } from "./justAdded";

export type { Kind } from "./justAdded";

export const KINDS: readonly { kind: Kind; label: string }[] = [
  { kind: "task", label: "Task" },
  { kind: "note", label: "Note" },
  { kind: "project", label: "Project" },
  { kind: "context", label: "Context" },
];

type Tone = "good" | "hint" | "bad" | "wait";

/** What the pill says, how it is coloured, and which bins it offers (`verb`, minus `except`). A
 * line the AI placed offers "Change to"; one it could not place offers "File as". */
export function pillOf(entry: AddEntry): {
  label: string;
  tone: Tone;
  verb: "Change to" | "File as" | null;
  except: Kind | null;
} {
  switch (entry.state) {
    case "sending":
      return { label: "Sending…", tone: "wait", verb: null, except: null };
    case "queued":
    case "processing":
      return { label: "Waiting…", tone: "wait", verb: null, except: null };
    case "guess":
      return { label: "Not sure", tone: "hint", verb: "File as", except: null };
    case "failed":
      return { label: "Failed", tone: "bad", verb: "File as", except: null };
    case "held":
      return { label: "Not filed", tone: "hint", verb: "File as", except: null };
    case "filed": {
      const kind = entry.created?.kind ?? null;
      const label = KINDS.find((k) => k.kind === kind)?.label ?? "Item";
      return { label: `Created ${label}`, tone: "good", verb: "Change to", except: kind };
    }
  }
}

/** True when the line is one the person has to look at: the AI could not place it. */
export function needsLook(entry: AddEntry): boolean {
  return entry.state === "guess" || entry.state === "failed" || entry.state === "held";
}

/** The sentence under a line that has more to say than its pill. */
export function detailOf(entry: AddEntry): string | null {
  switch (entry.state) {
    case "filed":
      return entry.enriching ? "The AI is tidying it up…" : null;
    case "failed":
      return entry.error ?? entry.reason;
    case "guess":
    case "held":
      return entry.reason;
    default:
      return null;
  }
}
